import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Head from 'react-helmet';
import { connect } from 'react-redux';
import styled from '@emotion/styled';
import numeral from 'numeral';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  ComposedChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
  RadialBarChart,
  RadialBar,
  PolarAngleAxis,
} from 'recharts';

import Menu from 'components/menu';
import BranchAdministrativeDivisionPicker from 'components/branch/branch-administrative-division-picker';
import BranchPlanEditor from 'components/branch/branch-plan-editor';
import Box from 'components/box';
import { Alert } from 'components/alerts';
import LoadingIndicator from 'components/loading-indicator';
import api from 'lib/api';
import { ROLES } from 'lib/config';
import { userSelector } from 'store/auth/auth-selectors';

/* ------------------------------------------------------------------ *
 * Palette
 * ------------------------------------------------------------------ */
const HOUSEHOLD_COLOR = '#3b6cf6'; // indigo-blue
const MEMBER_COLOR = '#0ea5a4'; // teal
const AVG_COLOR = '#f59e0b'; // amber
const AXIS_COLOR = '#98a2b3';
const GRID_COLOR = '#eef0f4';

// Pie palettes
const MALE_COLOR = '#3b6cf6';
const FEMALE_COLOR = '#ec4899';
const PAYING_COLOR = '#12b76a';
const INDIGENT_COLOR = '#f59e0b';
const RENEWAL_COLOR = '#0ea5a4';
const NEW_COLOR = '#8b5cf6';
const DISABLED_COLOR = '#a855f7';
const NOT_DISABLED_COLOR = '#cbd5e1';

// Membership-payment palette (revenue composition)
const REVENUE_COLOR = '#067647'; // emerald
const CONTRIBUTION_COLOR = '#12b76a'; // green
const REGISTRATION_COLOR = '#3b6cf6'; // blue
const PENALTY_COLOR = '#f04438'; // red
const OTHER_FEE_COLOR = '#f59e0b'; // amber

// Plan-vs-actual status palette. Bands are deliberately simple so a manager can read
// performance at a glance; tune the thresholds in planStatus() if policy changes.
const AHEAD_COLOR = '#12b76a'; // green  — met or exceeded target
const ON_TRACK_COLOR = '#3b6cf6'; // blue   — close to target
const BEHIND_COLOR = '#f79009'; // amber  — meaningfully short
const NEUTRAL_COLOR = '#98a2b3'; // grey   — no target / not measurable

const PLAN_STATUS = {
  ahead: { label: 'Ahead', color: AHEAD_COLOR, bg: '#e7f8ee', fg: '#067647' },
  on_track: { label: 'On track', color: ON_TRACK_COLOR, bg: '#eaf0ff', fg: '#2f5bd8' },
  behind: { label: 'Behind', color: BEHIND_COLOR, bg: '#fef0c7', fg: '#b54708' },
  none: { label: 'No target', color: NEUTRAL_COLOR, bg: '#f2f4f7', fg: '#667085' },
  na: { label: 'Not tracked', color: NEUTRAL_COLOR, bg: '#f2f4f7', fg: '#667085' },
};

// rate is actual/planned as a percentage (or null when actual isn't aggregated).
function planStatus({ planned, rate }) {
  if (!planned) return 'none';
  if (rate == null) return 'na';
  if (rate >= 100) return 'ahead';
  if (rate >= 80) return 'on_track';
  return 'behind';
}

// Red/green colour for a percentage: green once it's on track toward target, red when
// meaningfully behind. Used for the per-bar percentage labels and the actual bars.
const RATE_RED = '#f04438';
const RATE_GREEN = '#12b76a';
const RATE_GREEN_STRONG = '#067647';
function rateColor(rate) {
  if (rate == null) return '#98a2b3';
  if (rate >= 100) return RATE_GREEN_STRONG;
  if (rate >= 80) return RATE_GREEN;
  return RATE_RED;
}

/* ------------------------------------------------------------------ *
 * Layout
 * ------------------------------------------------------------------ */
const Page = styled.main`
  width: 100%;
  min-height: calc(100vh - ${props => props.theme.layout.menuHeight}px);
  background: linear-gradient(180deg, #f7f8fb 0%, #eef1f6 100%);
`;

const Content = styled.div`
  max-width: 1440px;
  margin: 0 auto;
  padding: 28px 40px 56px;

  @media (max-width: 720px) {
    padding: 20px 16px 40px;
  }
`;

/* ------------------------------------------------------------------ *
 * Header
 * ------------------------------------------------------------------ */
const HeaderCard = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  background: #fff;
  border: 1px solid #e7eaf0;
  border-radius: 16px;
  padding: 22px 26px;
  margin-bottom: 24px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
`;

const HeaderTitle = styled.h1`
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: #101828;
  line-height: 1.2;
`;

const HeaderSubtitle = styled.div`
  margin-top: 6px;
  font-size: 14px;
  color: #667085;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const Crumb = styled.span`
  color: #475467;
  font-weight: 500;
`;

const CrumbSep = styled.span`
  color: #cbd2dd;
`;

const PillRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 999px;
  background: ${props => props.bg || '#f2f4f7'};
  color: ${props => props.color || '#475467'};
  white-space: nowrap;
`;

const Dot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.color || '#12b76a'};
`;

/* ------------------------------------------------------------------ *
 * Total Members export (background job -> notify -> download)
 * ------------------------------------------------------------------ */
const ExportBtn = styled.button`
  appearance: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid ${props => (props.disabled ? '#d0d5dd' : MEMBER_COLOR)};
  background: ${props => (props.disabled ? '#f9fafb' : '#fff')};
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 600;
  color: ${props => (props.disabled ? '#98a2b3' : '#0b7a79')};
  cursor: ${props => (props.disabled ? 'default' : 'pointer')};
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;

  &:hover {
    ${props => !props.disabled && `
      background: #e3f7f5;
      border-color: #0b7a79;
    `}
  }
`;

// Tiny inline spinner for the in-progress button state.
const ExportSpinner = styled.span`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid #d0d5dd;
  border-top-color: #0b7a79;
  animation: export-spin 0.8s linear infinite;

  @keyframes export-spin {
    to { transform: rotate(360deg); }
  }
`;

const ExportBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
  border-radius: 12px;
  padding: 12px 18px;
  margin-bottom: 20px;
  font-size: 14px;
  font-weight: 500;
  background: ${props => (props.tone === 'success' ? '#e7f8ee' : '#eaf0ff')};
  color: ${props => (props.tone === 'success' ? '#067647' : '#2f5bd8')};
  border: 1px solid ${props => (props.tone === 'success' ? '#a6e9c5' : '#c7d7fe')};
`;

const ExportBannerLink = styled.button`
  appearance: none;
  border: none;
  background: none;
  padding: 0;
  font-size: 14px;
  font-weight: 700;
  color: inherit;
  text-decoration: underline;
  cursor: pointer;
`;

/* ------------------------------------------------------------------ *
 * KPI cards
 * ------------------------------------------------------------------ */
const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
`;

const KpiCard = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  background: #fff;
  border: 1px solid #e7eaf0;
  border-radius: 16px;
  padding: 20px 22px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  transition: transform 0.16s ease, box-shadow 0.16s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 28px rgba(16, 24, 40, 0.1);
  }
`;

const KpiIconWrap = styled.div`
  flex: 0 0 auto;
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  background: ${props => props.bg};
  color: ${props => props.color};
`;

const KpiBody = styled.div`
  min-width: 0;
`;

const KpiLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #98a2b3;
  margin-bottom: 4px;
`;

const KpiValue = styled.div`
  font-size: 30px;
  font-weight: 700;
  color: #101828;
  line-height: 1.1;
  letter-spacing: -0.5px;
`;

const KpiHint = styled.div`
  margin-top: 2px;
  font-size: 13px;
  color: #98a2b3;
`;

/* ------------------------------------------------------------------ *
 * Charts
 * ------------------------------------------------------------------ */
const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
`;

const ChartCard = styled.section`
  min-width: 0;
  background: #fff;
  border: 1px solid #e7eaf0;
  border-radius: 16px;
  padding: 22px 24px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  display: flex;
  flex-direction: column;
`;

const ChartHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
`;

const ChartTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #1d2939;
`;

const ChartSub = styled.span`
  font-size: 13px;
  color: #98a2b3;
  font-weight: 500;
  white-space: nowrap;
`;

const ChartBody = styled.div`
  flex: 1;
  min-height: 320px;
`;

const EmptyChart = styled.div`
  height: 320px;
  display: grid;
  place-items: center;
  text-align: center;
  color: #98a2b3;
  font-size: 14px;
  border: 1px dashed #e2e6ee;
  border-radius: 12px;
`;

/* ------------------------------------------------------------------ *
 * Distribution pies
 * ------------------------------------------------------------------ */
const SectionLabel = styled.div`
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #98a2b3;
  margin: 4px 2px 14px;
`;

// Six-per-row donut grid; steps down to 4 / 3 / 2 / 1 as the width shrinks.
const PieGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 1280px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (max-width: 760px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const PieCardEl = styled.div`
  background: #fff;
  border: 1px solid #e7eaf0;
  border-radius: 16px;
  padding: 18px 20px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  transition: transform 0.16s ease, box-shadow 0.16s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 28px rgba(16, 24, 40, 0.1);
  }
`;

const PieTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #1d2939;
  margin-bottom: 8px;
`;

const PieRelative = styled.div`
  position: relative;
`;

const PieCenter = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

const PieCenterValue = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #101828;
  line-height: 1;
`;

const PieCenterLabel = styled.div`
  font-size: 11px;
  color: #98a2b3;
  margin-top: 3px;
`;

const PieLegend = styled.div`
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PieLegendRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #475467;
`;

const PieLegendName = styled.span`
  font-weight: 500;
`;

const PieLegendVal = styled.span`
  margin-left: auto;
  font-weight: 700;
  color: #1d2939;
`;

const PieLegendPct = styled.span`
  width: 46px;
  text-align: right;
  color: #98a2b3;
  font-weight: 600;
`;

const PieEmpty = styled.div`
  height: 168px;
  display: grid;
  place-items: center;
  text-align: center;
  color: #98a2b3;
  font-size: 13px;
  border: 1px dashed #e2e6ee;
  border-radius: 12px;
`;

/* ------------------------------------------------------------------ *
 * Tooltip
 * ------------------------------------------------------------------ */
const TooltipBox = styled.div`
  background: #1d2939;
  color: #fff;
  border-radius: 10px;
  padding: 10px 12px;
  box-shadow: 0 8px 24px rgba(16, 24, 40, 0.24);
  font-size: 13px;
  min-width: 150px;
`;

const TooltipLabel = styled.div`
  font-weight: 700;
  margin-bottom: 8px;
  color: #fff;
`;

const TooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 5px;
  color: #d0d5dd;
`;

const TooltipValue = styled.span`
  margin-left: auto;
  font-weight: 700;
  color: #fff;
`;

/* ------------------------------------------------------------------ *
 * Centered state cards (loading / error / empty)
 * ------------------------------------------------------------------ */
const StateCard = styled.div`
  background: #fff;
  border: 1px solid #e7eaf0;
  border-radius: 16px;
  padding: 56px 24px;
  text-align: center;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
`;

const HintCard = styled(StateCard)`
  color: #667085;
  font-size: 15px;
`;

/* ------------------------------------------------------------------ *
 * Icons (inline, currentColor)
 * ------------------------------------------------------------------ */
function Icon({ name, size = 24 }) {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.9,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  };
  if (name === 'home') {
    return (
      <svg {...common} aria-hidden>
        <path d="M3 10.2 12 3l9 7.2" />
        <path d="M5 9.5V20a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9.5" />
        <path d="M9.5 21v-6h5v6" />
      </svg>
    );
  }
  if (name === 'users') {
    return (
      <svg {...common} aria-hidden>
        <circle cx="9" cy="8" r="3.2" />
        <path d="M3.5 20a5.5 5.5 0 0 1 11 0" />
        <path d="M16 5.2a3.2 3.2 0 0 1 0 6" />
        <path d="M17 14.4A5.5 5.5 0 0 1 20.5 20" />
      </svg>
    );
  }
  if (name === 'download') {
    return (
      <svg {...common} aria-hidden>
        <path d="M12 3v11" />
        <path d="m7.5 10.5 4.5 4.5 4.5-4.5" />
        <path d="M4 17v2.4a1.6 1.6 0 0 0 1.6 1.6h12.8a1.6 1.6 0 0 0 1.6-1.6V17" />
      </svg>
    );
  }
  if (name === 'members') {
    // A small "community" glyph: three people, distinct from the single beneficiaries icon.
    return (
      <svg {...common} aria-hidden>
        <circle cx="12" cy="7" r="2.6" />
        <circle cx="5" cy="9.5" r="2.1" />
        <circle cx="19" cy="9.5" r="2.1" />
        <path d="M7.5 19a4.5 4.5 0 0 1 9 0" />
        <path d="M2.5 18.5a3.6 3.6 0 0 1 4.2-3" />
        <path d="M21.5 18.5a3.6 3.6 0 0 0-4.2-3" />
      </svg>
    );
  }
  // ratio / average
  return (
    <svg {...common} aria-hidden>
      <path d="M5 19 19 5" />
      <circle cx="7.5" cy="7.5" r="2.5" />
      <circle cx="16.5" cy="16.5" r="2.5" />
    </svg>
  );
}

Icon.propTypes = {
  name: PropTypes.oneOf(['home', 'users', 'members', 'ratio', 'download']).isRequired,
  size: PropTypes.number,
};

Icon.defaultProps = { size: 24 };

/* ------------------------------------------------------------------ *
 * Helpers
 * ------------------------------------------------------------------ */
const MAX_BARS = 12;

function formatNumber(value) {
  return numeral(value || 0).format('0,0');
}

function formatDecimal(value) {
  return numeral(value || 0).format('0,0.00');
}

function formatCompact(value) {
  return numeral(value || 0)
    .format('0.[0]a')
    .toUpperCase();
}

// Ethiopian birr. Payment amounts are stored in the database multiplied by 100 (cents),
// so divide by 100 for display. Compact form for headline KPIs (e.g. 6.7M Br), full form
// for tooltips/legends.
function birr(value) {
  return (value || 0) / 100;
}

function formatBirr(value) {
  return `${formatNumber(birr(value))} Br`;
}

function formatBirrCompact(value) {
  return `${formatCompact(birr(value))} Br`;
}

function formatPercentValue(value) {
  return `${numeral(value || 0).format('0.[0]')}%`;
}

function titleCase(value) {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function formatFreshness(refreshedAt) {
  if (!refreshedAt) return null;
  const date = new Date(refreshedAt);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/* ------------------------------------------------------------------ *
 * Tooltip component
 * ------------------------------------------------------------------ */
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <TooltipBox>
      <TooltipLabel>{label}</TooltipLabel>
      {payload.map(entry => (
        <TooltipRow key={entry.dataKey}>
          <Dot color={entry.color} />
          <span>{entry.name}</span>
          <TooltipValue>{formatNumber(entry.value)}</TooltipValue>
        </TooltipRow>
      ))}
    </TooltipBox>
  );
}

ChartTooltip.propTypes = {
  active: PropTypes.bool,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  payload: PropTypes.arrayOf(PropTypes.object),
};

ChartTooltip.defaultProps = { active: false, label: '', payload: [] };

// Like ChartTooltip, but the mean-household-size series ("avg") is shown with decimals.
function DivisionTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <TooltipBox>
      <TooltipLabel>{label}</TooltipLabel>
      {payload.map(entry => (
        <TooltipRow key={entry.dataKey}>
          <Dot color={entry.color || entry.stroke} />
          <span>{entry.name}</span>
          <TooltipValue>
            {entry.dataKey === 'avg' ? formatDecimal(entry.value) : formatNumber(entry.value)}
          </TooltipValue>
        </TooltipRow>
      ))}
    </TooltipBox>
  );
}

DivisionTooltip.propTypes = {
  active: PropTypes.bool,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  payload: PropTypes.arrayOf(PropTypes.object),
};

DivisionTooltip.defaultProps = { active: false, label: '', payload: [] };

function formatPercent(value, total) {
  if (!total) return '0%';
  return `${Math.round((value / total) * 100)}%`;
}

function PieTooltip({ active, payload, valueFormatter }) {
  if (!active || !payload || !payload.length) return null;
  const entry = payload[0];
  return (
    <TooltipBox>
      <TooltipRow>
        <Dot color={entry.payload.color} />
        <span>{entry.name}</span>
        <TooltipValue>{valueFormatter(entry.value)}</TooltipValue>
      </TooltipRow>
    </TooltipBox>
  );
}

PieTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(PropTypes.object),
  valueFormatter: PropTypes.func,
};

PieTooltip.defaultProps = { active: false, payload: [], valueFormatter: formatNumber };

function DonutCard({ title, centerLabel, data, valueFormatter, centerFormatter }) {
  const total = data.reduce((sum, slice) => sum + (slice.value || 0), 0);

  return (
    <PieCardEl>
      <PieTitle>{title}</PieTitle>
      {total > 0 ? (
        <>
          <PieRelative>
            <ResponsiveContainer width="100%" height={168}>
              <PieChart>
                <Pie
                  data={data}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={50}
                  outerRadius={72}
                  paddingAngle={data.length > 1 ? 2 : 0}
                  stroke="none"
                  startAngle={90}
                  endAngle={-270}
                >
                  {data.map(slice => (
                    <Cell key={slice.name} fill={slice.color} />
                  ))}
                </Pie>
                <Tooltip content={<PieTooltip valueFormatter={valueFormatter} />} />
              </PieChart>
            </ResponsiveContainer>
            <PieCenter>
              <PieCenterValue>{centerFormatter(total)}</PieCenterValue>
              <PieCenterLabel>{centerLabel}</PieCenterLabel>
            </PieCenter>
          </PieRelative>
          <PieLegend>
            {data.map(slice => (
              <PieLegendRow key={slice.name}>
                <Dot color={slice.color} />
                <PieLegendName>{slice.name}</PieLegendName>
                <PieLegendVal>{valueFormatter(slice.value)}</PieLegendVal>
                <PieLegendPct>{formatPercent(slice.value, total)}</PieLegendPct>
              </PieLegendRow>
            ))}
          </PieLegend>
        </>
      ) : (
        <PieEmpty>No data for this scope.</PieEmpty>
      )}
    </PieCardEl>
  );
}

DonutCard.propTypes = {
  title: PropTypes.string.isRequired,
  centerLabel: PropTypes.string.isRequired,
  data: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.number,
    color: PropTypes.string,
  })).isRequired,
  valueFormatter: PropTypes.func,
  centerFormatter: PropTypes.func,
};

DonutCard.defaultProps = {
  valueFormatter: formatNumber,
  centerFormatter: formatCompact,
};

/* ------------------------------------------------------------------ *
 * KPI cards
 * ------------------------------------------------------------------ */
function KpiCards({ kpis }) {
  const households = kpis.enrolledHouseholds || 0;
  // Beneficiaries = enrolled Members excluding household (one SELF head per
  // household). Members = beneficiaries + households (every member: beneficiaries plus the
  // one head per household). Derive each from the other so the three cards always add up.
  const beneficiaries =
    kpis.enrolledBeneficiaries != null
      ? kpis.enrolledBeneficiaries
      : Math.max((kpis.enrolledMembers || 0) - households, 0);
  const members =
    kpis.enrolledMembers != null ? kpis.enrolledMembers : beneficiaries + households;
  const avg = kpis.averageMembersPerHousehold || 0;

  return (
    <KpiGrid>
      <KpiCard>
        <KpiIconWrap bg="#eaf0ff" color={HOUSEHOLD_COLOR}>
          <Icon name="home" />
        </KpiIconWrap>
        <KpiBody>
          <KpiLabel>Enrolled households</KpiLabel>
          <KpiValue>{formatNumber(households)}</KpiValue>
          <KpiHint>Distinct households covered</KpiHint>
        </KpiBody>
      </KpiCard>

      <KpiCard>
        <KpiIconWrap bg="#e3f7f5" color={MEMBER_COLOR}>
          <Icon name="users" />
        </KpiIconWrap>
        <KpiBody>
          <KpiLabel>Beneficiaries</KpiLabel>
          <KpiValue>{formatNumber(beneficiaries)}</KpiValue>
          <KpiHint>Members excluding household</KpiHint>
        </KpiBody>
      </KpiCard>

      <KpiCard>
        <KpiIconWrap bg="#efeaff" color={NEW_COLOR}>
          <Icon name="members" />
        </KpiIconWrap>
        <KpiBody>
          <KpiLabel>Members</KpiLabel>
          <KpiValue>{formatNumber(members)}</KpiValue>
          <KpiHint>Beneficiaries + households</KpiHint>
        </KpiBody>
      </KpiCard>

      <KpiCard>
        <KpiIconWrap bg="#fdf1dc" color={AVG_COLOR}>
          <Icon name="ratio" />
        </KpiIconWrap>
        <KpiBody>
          <KpiLabel>Avg. members / household</KpiLabel>
          <KpiValue>{formatDecimal(avg)}</KpiValue>
          <KpiHint>Mean household size</KpiHint>
        </KpiBody>
      </KpiCard>
    </KpiGrid>
  );
}

KpiCards.propTypes = {
  kpis: PropTypes.shape({
    enrolledHouseholds: PropTypes.number,
    enrolledMembers: PropTypes.number,
    enrolledBeneficiaries: PropTypes.number,
    averageMembersPerHousehold: PropTypes.number,
  }).isRequired,
};

/* ------------------------------------------------------------------ *
 * Membership payments
 * ------------------------------------------------------------------ */
const PaymentsGrid = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr);
  gap: 20px;
  margin-bottom: 24px;

  @media (max-width: 1080px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const PayCard = styled.div`
  background: #fff;
  border: 1px solid #e7eaf0;
  border-radius: 16px;
  padding: 20px 22px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  display: flex;
  flex-direction: column;
`;

const PayCardTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #1d2939;
  margin-bottom: 14px;
`;

const PayGaugeFoot = styled.div`
  margin-top: 10px;
  text-align: center;
  font-size: 13px;
  color: #667085;
`;

const PayHero = styled.div`
  background: linear-gradient(135deg, ${REVENUE_COLOR} 0%, #12b76a 100%);
  border-radius: 14px;
  padding: 22px 22px 18px;
  color: #fff;
  box-shadow: 0 10px 24px rgba(6, 118, 71, 0.22);
`;

const PayHeroLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  opacity: 0.86;
`;

const PayHeroValue = styled.div`
  font-size: 34px;
  font-weight: 800;
  line-height: 1.04;
  margin-top: 8px;
  letter-spacing: -0.6px;
`;

const PayHeroSub = styled.div`
  font-size: 13px;
  opacity: 0.85;
  margin-top: 6px;
`;

const PayStats = styled.div`
  margin-top: 14px;
`;

const PayStatRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 13px 2px;
  border-bottom: 1px solid #f0f2f6;

  &:last-child {
    border-bottom: none;
  }
`;

const PayStatLabel = styled.span`
  font-size: 13px;
  color: #667085;
`;

const PayStatValue = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 700;
  color: #1d2939;
`;

const RatePill = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: #067647;
  background: #e7f8ee;
  border-radius: 999px;
  padding: 3px 9px;
`;

function PaymentSection({ payments }) {
  const totalCollected = payments.totalCollected || 0;
  const paidHouseholds = payments.paidHouseholds || 0;
  const paymentRate = payments.paymentRate || 0;
  const averagePaid = payments.averagePaidPerHousehold || 0;
  const breakdown = payments.breakdown || {};

  const composition = [
    { name: 'Contribution', value: breakdown.contribution || 0, color: CONTRIBUTION_COLOR },
    { name: 'Registration', value: breakdown.registration || 0, color: REGISTRATION_COLOR },
    { name: 'Penalty', value: breakdown.penalty || 0, color: PENALTY_COLOR },
    { name: 'Other fees', value: breakdown.other || 0, color: OTHER_FEE_COLOR },
  ];
  // Estimate enrolled households from paid + rate so the gauge can show paid-of-total.
  const enrolledHouseholds = paymentRate > 0
    ? Math.round(paidHouseholds / (paymentRate / 100))
    : paidHouseholds;

  return (
    <>
      <SectionLabel>Membership payments</SectionLabel>
      <PaymentsGrid>
        {/* 1 — headline revenue + key stats */}
        <PayCard>
          <PayHero>
            <PayHeroLabel>Total collected</PayHeroLabel>
            <PayHeroValue>{formatBirrCompact(totalCollected)}</PayHeroValue>
            <PayHeroSub>{formatBirr(totalCollected)}</PayHeroSub>
          </PayHero>

          <PayStats>
            <PayStatRow>
              <PayStatLabel>Households paid</PayStatLabel>
              <PayStatValue>
                {formatNumber(paidHouseholds)}
                <RatePill>{formatPercentValue(paymentRate)}</RatePill>
              </PayStatValue>
            </PayStatRow>
            <PayStatRow>
              <PayStatLabel>Avg. paid / household</PayStatLabel>
              <PayStatValue>{formatBirr(averagePaid)}</PayStatValue>
            </PayStatRow>
          </PayStats>
        </PayCard>

        {/* 2 — payment rate gauge */}
        <PayCard>
          <PayCardTitle>Payment rate</PayCardTitle>
          <PieRelative>
            <ResponsiveContainer width="100%" height={196}>
              <RadialBarChart innerRadius="68%" outerRadius="100%" data={[{ value: Math.min(paymentRate, 100) }]} startAngle={90} endAngle={-270}>
                <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                <RadialBar background={{ fill: '#eef0f4' }} dataKey="value" cornerRadius={12} fill={REVENUE_COLOR} />
              </RadialBarChart>
            </ResponsiveContainer>
            <PieCenter>
              <PieCenterValue>{formatPercentValue(paymentRate)}</PieCenterValue>
              <PieCenterLabel>of households</PieCenterLabel>
            </PieCenter>
          </PieRelative>
          <PayGaugeFoot>
            {`${formatNumber(paidHouseholds)} of ${formatNumber(enrolledHouseholds)} households paid`}
          </PayGaugeFoot>
        </PayCard>

        {/* 3 — revenue composition donut */}
        <DonutCard
          title="Revenue composition"
          centerLabel="collected"
          data={composition}
          valueFormatter={formatBirr}
          centerFormatter={formatBirrCompact}
        />
      </PaymentsGrid>
    </>
  );
}

PaymentSection.propTypes = {
  payments: PropTypes.shape({
    totalCollected: PropTypes.number,
    paidHouseholds: PropTypes.number,
    paymentRate: PropTypes.number,
    averagePaidPerHousehold: PropTypes.number,
    breakdown: PropTypes.object,
  }).isRequired,
};

/* ------------------------------------------------------------------ *
 * Sliding-scale financial report
 * ------------------------------------------------------------------ */
const SCALE_PALETTE = [
  '#3b6cf6', '#12b76a', '#f59e0b', '#8b5cf6', '#ec4899', '#0ea5a4', '#f04438', '#64748b',
];

const ScaleCard = styled.section`
  background: #fff;
  border: 1px solid #e7eaf0;
  border-radius: 16px;
  margin-bottom: 24px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  overflow: hidden;
`;

const ScaleTableWrap = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const ScaleTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

const Th = styled.th`
  text-align: ${props => props.align || 'left'};
  padding: 12px 22px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #98a2b3;
  background: #fbfbfd;
  border-bottom: 1px solid #eef0f4;
  white-space: nowrap;
`;

const Td = styled.td`
  text-align: ${props => props.align || 'left'};
  padding: 13px 22px;
  color: #1d2939;
  border-bottom: 1px solid #f4f6f9;
  white-space: nowrap;
`;

const ScaleNameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
`;

const TotalTd = styled(Td)`
  font-weight: 700;
  border-bottom: none;
  background: #fbfbfd;
`;

const ScaleChartWrap = styled.div`
  padding: 20px 22px 6px;
  border-bottom: 1px solid #f0f2f6;
`;

const ScaleChartTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #1d2939;
  margin-bottom: 14px;
`;

// Turns SCALE_NAMES like TAX_PAYER_A into "Tax Payer A" for display.
function humanizeScale(name) {
  if (!name) return 'Unspecified';
  return name
    .split('_')
    .map(word => (word ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : ''))
    .join(' ');
}

// Currency tooltip for the sliding-scale bar chart (value is stored in cents).
function ScaleAmountTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const entry = payload[0];
  return (
    <TooltipBox>
      <TooltipRow>
        <Dot color={entry.payload.fill} />
        <span>{entry.payload.name}</span>
        <TooltipValue>{formatBirr(entry.value)}</TooltipValue>
      </TooltipRow>
    </TooltipBox>
  );
}

ScaleAmountTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(PropTypes.object),
};

ScaleAmountTooltip.defaultProps = { active: false, payload: [] };

function SlidingScaleReport({ scales }) {
  const rows = [...scales].sort((a, b) => (b.amount || 0) - (a.amount || 0));
  const totals = rows.reduce(
    (acc, row) => ({
      households: acc.households + (row.households || 0),
      members: acc.members + (row.members || 0),
      amount: acc.amount + (row.amount || 0),
    }),
    { households: 0, members: 0, amount: 0 },
  );

  // Top scales by amount for the chart (the table keeps the full detail below).
  const chartData = rows.slice(0, 8).map((row, index) => ({
    name: humanizeScale(row.name),
    amount: row.amount || 0,
    fill: SCALE_PALETTE[index % SCALE_PALETTE.length],
  }));
  const chartHeight = Math.max(160, chartData.length * 44 + 24);

  return (
    <>
      <SectionLabel>Financial report by sliding scale</SectionLabel>
      <ScaleCard>
        {rows.length ? (
          <>
            <ScaleChartWrap>
              <ScaleChartTitle>Collected by sliding scale</ScaleChartTitle>
              <ResponsiveContainer width="100%" height={chartHeight}>
                <BarChart
                  data={chartData}
                  layout="vertical"
                  margin={{ top: 0, right: 16, left: 8, bottom: 0 }}
                  barCategoryGap="28%"
                >
                  <CartesianGrid stroke={GRID_COLOR} horizontal={false} />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 11, fill: AXIS_COLOR }}
                    tickLine={false}
                    axisLine={{ stroke: GRID_COLOR }}
                    tickFormatter={formatBirrCompact}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={{ fontSize: 12, fill: '#475467' }}
                    tickLine={false}
                    axisLine={false}
                    width={150}
                  />
                  <Tooltip cursor={{ fill: 'rgba(59,108,246,0.06)' }} content={<ScaleAmountTooltip />} />
                  <Bar dataKey="amount" radius={[0, 5, 5, 0]} maxBarSize={26}>
                    {chartData.map(cell => (
                      <Cell key={cell.name} fill={cell.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ScaleChartWrap>
          <ScaleTableWrap>
            <ScaleTable>
              <thead>
                <tr>
                  <Th>Sliding scale</Th>
                  <Th align="right">Households</Th>
                  <Th align="right">Members</Th>
                  <Th align="right">Collected</Th>
                  <Th align="right">% of revenue</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={row.name || `scale-${index}`}>
                    <Td>
                      <ScaleNameCell>
                        <Dot color={SCALE_PALETTE[index % SCALE_PALETTE.length]} />
                        {humanizeScale(row.name)}
                      </ScaleNameCell>
                    </Td>
                    <Td align="right">{formatNumber(row.households)}</Td>
                    <Td align="right">{formatNumber(row.members)}</Td>
                    <Td align="right">{formatBirr(row.amount)}</Td>
                    <Td align="right">{formatPercent(row.amount, totals.amount)}</Td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <TotalTd>Total</TotalTd>
                  <TotalTd align="right">{formatNumber(totals.households)}</TotalTd>
                  <TotalTd align="right">{formatNumber(totals.members)}</TotalTd>
                  <TotalTd align="right">{formatBirr(totals.amount)}</TotalTd>
                  <TotalTd align="right">100%</TotalTd>
                </tr>
              </tfoot>
            </ScaleTable>
          </ScaleTableWrap>
          </>
        ) : (
          <PieEmpty style={{ margin: 18 }}>No sliding-scale payments for this scope.</PieEmpty>
        )}
      </ScaleCard>
    </>
  );
}

SlidingScaleReport.propTypes = {
  scales: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    households: PropTypes.number,
    members: PropTypes.number,
    amount: PropTypes.number,
  })).isRequired,
};

/* ------------------------------------------------------------------ *
 * Enrollment Rate (planned target vs actual enrolled)
 * ------------------------------------------------------------------ */
const PlanHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 4px 2px 14px;
`;

const PlanHeadLabel = styled.div`
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #98a2b3;
`;

const EditTargetsBtn = styled.button`
  appearance: none;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border: 1px solid #d0d5dd;
  background: #fff;
  border-radius: 8px;
  padding: 7px 13px;
  font-size: 13px;
  font-weight: 600;
  color: #344054;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover {
    border-color: #3b6cf6;
    color: #2f5bd8;
  }
`;

const PlanGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
  margin-bottom: 24px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const PlanCard = styled.section`
  background: #fff;
  border: 1px solid #e7eaf0;
  border-radius: 16px;
  padding: 22px 24px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
`;

const PlanCardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
`;

const PlanCardTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #1d2939;
`;

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 999px;
  background: ${props => props.bg};
  color: ${props => props.fg};
  white-space: nowrap;
`;

const PlanHeadline = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 4px;
`;

const PlanActual = styled.span`
  font-size: 30px;
  font-weight: 800;
  color: #101828;
  line-height: 1.1;
  letter-spacing: -0.5px;
`;

const PlanPlanned = styled.span`
  font-size: 14px;
  color: #667085;
  font-weight: 500;
`;

const PlanRate = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${props => props.color};
  margin-bottom: 12px;
`;

// Planned bars are muted grey; actual bars are coloured green/red by achievement (see
// rateColor), so the comparison reads at a glance.
const PLANNED_BAR_COLOR = '#cbd5e1';

const PlanEmpty = styled.div`
  background: #fff;
  border: 1px dashed #d8dde7;
  border-radius: 16px;
  padding: 28px 24px;
  text-align: center;
  color: #667085;
  font-size: 14px;
  margin-bottom: 24px;
`;

const SetTargetsBtn = styled(EditTargetsBtn)`
  margin-top: 14px;
`;

function fillPct(rate) {
  if (rate == null) return 0;
  return Math.max(0, Math.min(rate, 100));
}

// The achievement percentage drawn above each actual bar, coloured green/red by value.
function renderRateLabel({ x, y, width, value }) {
  if (value == null) return null;
  return (
    <text
      x={x + width / 2}
      y={y - 7}
      textAnchor="middle"
      fontSize={11}
      fontWeight={700}
      fill={rateColor(value)}
    >
      {`${Math.round(value)}%`}
    </text>
  );
}

renderRateLabel.propTypes = {
  x: PropTypes.number,
  y: PropTypes.number,
  width: PropTypes.number,
  value: PropTypes.number,
};

renderRateLabel.defaultProps = { x: 0, y: 0, width: 0, value: null };

// Grouped planned-vs-actual bars: one cluster per dimension (Total + breakdowns). Grey is
// the target; the actual bar is coloured green/red by how it tracks against target, with
// the achievement percentage labelled above it.
function PlanComparisonChart({ rows }) {
  return (
    <ResponsiveContainer width="100%" height={244}>
      <BarChart data={rows} margin={{ top: 20, right: 8, left: 0, bottom: 4 }} barGap={3} barCategoryGap="24%">
        <CartesianGrid stroke={GRID_COLOR} vertical={false} />
        <XAxis
          dataKey="name"
          tick={{ fontSize: 11, fill: AXIS_COLOR }}
          tickLine={false}
          axisLine={{ stroke: GRID_COLOR }}
          interval={0}
        />
        <YAxis
          tick={{ fontSize: 11, fill: AXIS_COLOR }}
          tickLine={false}
          axisLine={false}
          width={42}
          tickFormatter={formatCompact}
        />
        <Tooltip cursor={{ fill: 'rgba(59,108,246,0.06)' }} content={<ChartTooltip />} />
        <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 6 }} />
        <Bar dataKey="planned" name="Planned" fill={PLANNED_BAR_COLOR} radius={[4, 4, 0, 0]} maxBarSize={26} />
        <Bar dataKey="actual" name="Actual" fill={RATE_GREEN} radius={[4, 4, 0, 0]} maxBarSize={26}>
          {rows.map(row => (
            <Cell key={row.name} fill={rateColor(row.rate)} />
          ))}
          <LabelList dataKey="rate" content={renderRateLabel} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

PlanComparisonChart.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
};

// A radial gauge for a single rate (members): the arc fills toward 100% of target.
function PlanGauge({ metric, color }) {
  const rate = metric.rate || 0;
  const data = [{ name: 'rate', value: fillPct(rate) }];

  return (
    <PieRelative>
      <ResponsiveContainer width="100%" height={236}>
        <RadialBarChart innerRadius="66%" outerRadius="100%" data={data} startAngle={90} endAngle={-270}>
          <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
          <RadialBar background={{ fill: '#eef0f4' }} dataKey="value" cornerRadius={12} fill={color} />
        </RadialBarChart>
      </ResponsiveContainer>
      <PieCenter>
        <PieCenterValue>{metric.planned ? formatPercentValue(rate) : '—'}</PieCenterValue>
        <PieCenterLabel>of target</PieCenterLabel>
      </PieCenter>
    </PieRelative>
  );
}

PlanGauge.propTypes = {
  metric: PropTypes.object.isRequired,
  color: PropTypes.string.isRequired,
};

// A planned-vs-actual card for one beneficiary type. Households get a grouped bar chart
// across the breakdowns; members are a single target shown as a radial gauge.
function PlanMetricCard({ title, noun, metric, showBreakdowns }) {
  const status = planStatus(metric);
  const pill = PLAN_STATUS[status];
  const byType = metric.byEnrollmentType || {};
  const byCategory = metric.byCategory || {};

  const rows = [
    { name: 'Total', planned: metric.planned || 0, actual: metric.actual || 0, rate: metric.rate },
    { name: 'New', planned: (byType.new || {}).planned || 0, actual: (byType.new || {}).actual || 0, rate: (byType.new || {}).rate },
    { name: 'Renewal', planned: (byType.renewal || {}).planned || 0, actual: (byType.renewal || {}).actual || 0, rate: (byType.renewal || {}).rate },
    { name: 'Indigent', planned: (byCategory.indigent || {}).planned || 0, actual: (byCategory.indigent || {}).actual || 0, rate: (byCategory.indigent || {}).rate },
    { name: 'Paying', planned: (byCategory.paying || {}).planned || 0, actual: (byCategory.paying || {}).actual || 0, rate: (byCategory.paying || {}).rate },
  ];

  return (
    <PlanCard>
      <PlanCardTop>
        <PlanCardTitle>{title}</PlanCardTitle>
        <StatusPill bg={pill.bg} fg={pill.fg}>
          <Dot color={pill.color} />
          {pill.label}
        </StatusPill>
      </PlanCardTop>

      <PlanHeadline>
        <PlanActual>{formatNumber(metric.actual)}</PlanActual>
        <PlanPlanned>{`/ ${formatNumber(metric.planned)} ${noun} planned`}</PlanPlanned>
      </PlanHeadline>
      <PlanRate color={pill.color}>
        {metric.planned ? `${formatPercentValue(metric.rate)} of target` : 'No target set for this scope'}
      </PlanRate>

      {showBreakdowns
        ? <PlanComparisonChart rows={rows} />
        : <PlanGauge metric={metric} color={pill.color} />}
    </PlanCard>
  );
}

PlanMetricCard.propTypes = {
  title: PropTypes.string.isRequired,
  noun: PropTypes.string.isRequired,
  metric: PropTypes.object.isRequired,
  showBreakdowns: PropTypes.bool.isRequired,
};

function PlanPerformance({ plan, canEdit, onEditPlan }) {
  const hasPlan = plan && plan.hasPlan;

  return (
    <>
      <PlanHead>
        <PlanHeadLabel>Enrollment Rate</PlanHeadLabel>
        {canEdit && hasPlan ? (
          <EditTargetsBtn type="button" onClick={onEditPlan}>Edit plans</EditTargetsBtn>
        ) : null}
      </PlanHead>

      {hasPlan ? (
        <PlanGrid>
          <PlanMetricCard title="Households" noun="households" metric={plan.households || {}} showBreakdowns />
          {/* Members are a single target per scope — no new/renewal or indigent/paying split. */}
          <PlanMetricCard title="Members" noun="members" metric={plan.members || {}} showBreakdowns={false} />
        </PlanGrid>
      ) : (
        <PlanEmpty>
          No enrollment targets have been set for this scope and year.
          {canEdit ? (
            <div>
              <SetTargetsBtn type="button" onClick={onEditPlan}>Set targets</SetTargetsBtn>
            </div>
          ) : null}
        </PlanEmpty>
      )}
    </>
  );
}

PlanPerformance.propTypes = {
  plan: PropTypes.object,
  canEdit: PropTypes.bool.isRequired,
  onEditPlan: PropTypes.func.isRequired,
};

PlanPerformance.defaultProps = { plan: null };

/* ------------------------------------------------------------------ *
 * Report
 * ------------------------------------------------------------------ */
// Export status strip shown under the header while a Total Members export is being
// prepared in the background, and once it's ready (with a manual re-download link in
// case the browser blocked the automatic one).
function ExportStatus({ memberExport, onDownload }) {
  if (!memberExport) return null;

  if (memberExport.status === 'queued' || memberExport.status === 'processing') {
    return (
      <ExportBanner>
        <ExportSpinner />
        Preparing your Total Members export in the background — the download will start
        automatically when it&apos;s ready. You can keep using the dashboard.
      </ExportBanner>
    );
  }

  if (memberExport.status === 'completed') {
    return (
      <ExportBanner tone="success">
        {`Export ready${memberExport.rowCount != null ? ` — ${formatNumber(memberExport.rowCount)} members` : ''}. Your download has started.`}
        <ExportBannerLink type="button" onClick={onDownload}>
          Download again
        </ExportBannerLink>
      </ExportBanner>
    );
  }

  return null;
}

ExportStatus.propTypes = {
  memberExport: PropTypes.shape({
    status: PropTypes.string,
    rowCount: PropTypes.number,
  }),
  onDownload: PropTypes.func.isRequired,
};

ExportStatus.defaultProps = { memberExport: null };

function BranchDashboardReport({ report, canEdit, onEditPlan, memberExport, onExportMembers, onDownloadExport }) {
  const isExporting = !!memberExport
    && (memberExport.status === 'queued' || memberExport.status === 'processing');
  const divisionBreakdown = report.divisionBreakdown || [];

  const childLevel = divisionBreakdown.length ? divisionBreakdown[0].level : null;
  const levelPlural = childLevel ? `${childLevel}s` : 'divisions';

  const sortedBreakdown = [...divisionBreakdown].sort(
    (a, b) => (b.enrolledHouseholds || 0) - (a.enrolledHouseholds || 0),
  );
  // Mean household size per division (members / households) for the trend line overlay.
  const barData = sortedBreakdown.slice(0, MAX_BARS).map(row => ({
    ...row,
    avg: row.enrolledHouseholds
      ? Number((row.enrolledMembers / row.enrolledHouseholds).toFixed(2))
      : 0,
  }));
  const hiddenCount = sortedBreakdown.length - barData.length;
  const breakdownSub = hiddenCount > 0
    ? `Top ${MAX_BARS} of ${sortedBreakdown.length} ${levelPlural}`
    : `${sortedBreakdown.length} ${levelPlural}`;

  const freshness = formatFreshness(report.refreshedAt);

  const distribution = report.distribution || {};
  const memberGender = distribution.memberGender || {};
  const headGender = distribution.householdHeadGender || {};
  const payingType = distribution.payingType || {};
  const enrollmentType = distribution.enrollmentType || {};
  const memberDisability = distribution.memberDisability || {};
  const householdDisability = distribution.householdDisability || {};

  // Composition donuts, split into a Members group and a Households group so the section
  // reads as two clear, comparable sets rather than one long mixed row.
  const memberPies = [
    {
      title: 'Members by gender',
      centerLabel: 'members',
      data: [
        { name: 'Male', value: memberGender.male || 0, color: MALE_COLOR },
        { name: 'Female', value: memberGender.female || 0, color: FEMALE_COLOR },
      ],
    },
    {
      title: 'Members by disability',
      centerLabel: 'members',
      data: [
        { name: 'Disabled', value: memberDisability.disabled || 0, color: DISABLED_COLOR },
        { name: 'Not disabled', value: memberDisability.notDisabled || 0, color: NOT_DISABLED_COLOR },
      ],
    },
  ];

  const householdPies = [
    {
      title: 'Households by head gender',
      centerLabel: 'households',
      data: [
        { name: 'Male', value: headGender.male || 0, color: MALE_COLOR },
        { name: 'Female', value: headGender.female || 0, color: FEMALE_COLOR },
      ],
    },
    {
      title: 'Households by disability',
      centerLabel: 'households',
      data: [
        { name: 'With disabled member', value: householdDisability.disabled || 0, color: DISABLED_COLOR },
        { name: 'Without', value: householdDisability.notDisabled || 0, color: NOT_DISABLED_COLOR },
      ],
    },
    {
      title: 'Households by paying type',
      centerLabel: 'households',
      data: [
        { name: 'Paying', value: payingType.paying || 0, color: PAYING_COLOR },
        { name: 'Indigent', value: payingType.indigent || 0, color: INDIGENT_COLOR },
      ],
    },
    {
      title: 'Households by enrollment type',
      centerLabel: 'households',
      data: [
        { name: 'Renewal', value: enrollmentType.renewal || 0, color: RENEWAL_COLOR },
        { name: 'New', value: enrollmentType.new || 0, color: NEW_COLOR },
      ],
    },
  ];

  return (
    <>
      <HeaderCard>
        <div>
          <HeaderTitle>{report.branchName}</HeaderTitle>
          <HeaderSubtitle>
            {report.administrativeDivisionName ? (
              <>
                <Crumb>{report.branchName}</Crumb>
                <CrumbSep>›</CrumbSep>
                <Crumb>{report.administrativeDivisionName}</Crumb>
              </>
            ) : (
              <Crumb>Whole branch</Crumb>
            )}
          </HeaderSubtitle>
        </div>

        <PillRow>
          <Pill bg="#eaf0ff" color={HOUSEHOLD_COLOR}>
            {`Enrollment year ${report.enrollmentYear}`}
          </Pill>
          {freshness ? (
            <Pill bg="#e7f8ee" color="#067647">
              <Dot color="#12b76a" />
              {`Updated ${freshness}`}
            </Pill>
          ) : (
            <Pill bg="#fef0c7" color="#b54708">
              <Dot color="#f79009" />
              Data not yet computed
            </Pill>
          )}
          <ExportBtn type="button" onClick={onExportMembers} disabled={isExporting}>
            {isExporting ? <ExportSpinner /> : <Icon name="download" size={16} />}
            {isExporting ? 'Preparing export…' : 'Export Total Members (CSV)'}
          </ExportBtn>
        </PillRow>
      </HeaderCard>

      <ExportStatus memberExport={memberExport} onDownload={onDownloadExport} />

      <KpiCards kpis={report.kpis} />

      <PlanPerformance plan={report.plan} canEdit={canEdit} onEditPlan={onEditPlan} />

      <PaymentSection payments={report.payments || {}} />

      <SlidingScaleReport scales={report.slidingScales || []} />

      <SectionLabel>Composition</SectionLabel>
      <PieGrid>
        {[...memberPies, ...householdPies].map(pie => (
          <DonutCard key={pie.title} title={pie.title} centerLabel={pie.centerLabel} data={pie.data} />
        ))}
      </PieGrid>

      <ChartsGrid>
        <ChartCard>
          <ChartHead>
            <ChartTitle>{`Enrollment by ${childLevel ? titleCase(childLevel) : 'division'}`}</ChartTitle>
            <ChartSub>{breakdownSub}</ChartSub>
          </ChartHead>
          <ChartBody>
            {barData.length ? (
              <ResponsiveContainer width="100%" height={340}>
                <ComposedChart data={barData} margin={{ top: 4, right: 8, bottom: 8, left: 0 }} barCategoryGap="22%">
                  <defs>
                    <linearGradient id="bar-households" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={HOUSEHOLD_COLOR} stopOpacity={0.95} />
                      <stop offset="100%" stopColor={HOUSEHOLD_COLOR} stopOpacity={0.6} />
                    </linearGradient>
                    <linearGradient id="bar-members" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={MEMBER_COLOR} stopOpacity={0.95} />
                      <stop offset="100%" stopColor={MEMBER_COLOR} stopOpacity={0.6} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke={GRID_COLOR} vertical={false} />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11, fill: AXIS_COLOR }}
                    tickLine={false}
                    axisLine={{ stroke: GRID_COLOR }}
                    interval={0}
                    angle={-25}
                    textAnchor="end"
                    height={64}
                  />
                  <YAxis
                    yAxisId="count"
                    tick={{ fontSize: 11, fill: AXIS_COLOR }}
                    tickLine={false}
                    axisLine={false}
                    width={44}
                    tickFormatter={formatCompact}
                  />
                  <YAxis
                    yAxisId="avg"
                    orientation="right"
                    tick={{ fontSize: 11, fill: AVG_COLOR }}
                    tickLine={false}
                    axisLine={false}
                    width={36}
                    tickFormatter={value => formatDecimal(value)}
                  />
                  <Tooltip cursor={{ fill: 'rgba(59,108,246,0.06)' }} content={<DivisionTooltip />} />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 13, paddingTop: 6 }} />
                  <Bar yAxisId="count" dataKey="enrolledHouseholds" name="Households" fill="url(#bar-households)" radius={[5, 5, 0, 0]} maxBarSize={34} />
                  <Bar yAxisId="count" dataKey="enrolledMembers" name="Members" fill="url(#bar-members)" radius={[5, 5, 0, 0]} maxBarSize={34} />
                  <Line
                    yAxisId="avg"
                    type="monotone"
                    dataKey="avg"
                    name="Mean household size"
                    stroke={AVG_COLOR}
                    strokeWidth={2.5}
                    dot={{ r: 3, fill: AVG_COLOR, strokeWidth: 0 }}
                    activeDot={{ r: 5 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart>No administrative division breakdown for this scope.</EmptyChart>
            )}
          </ChartBody>
        </ChartCard>
      </ChartsGrid>
    </>
  );
}

BranchDashboardReport.propTypes = {
  report: PropTypes.shape({
    branchName: PropTypes.string,
    administrativeDivisionName: PropTypes.string,
    enrollmentYear: PropTypes.number,
    refreshedAt: PropTypes.string,
    kpis: PropTypes.object,
    plan: PropTypes.object,
    payments: PropTypes.object,
    slidingScales: PropTypes.arrayOf(PropTypes.object),
    distribution: PropTypes.object,
    divisionBreakdown: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  canEdit: PropTypes.bool.isRequired,
  onEditPlan: PropTypes.func.isRequired,
  memberExport: PropTypes.shape({
    status: PropTypes.string,
    rowCount: PropTypes.number,
  }),
  onExportMembers: PropTypes.func.isRequired,
  onDownloadExport: PropTypes.func.isRequired,
};

BranchDashboardReport.defaultProps = { memberExport: null };

/* ------------------------------------------------------------------ *
 * View
 * ------------------------------------------------------------------ */
function BranchDashboardView({ user }) {
  const [report, setReport] = useState(null);
  const [selection, setSelection] = useState(null);
  const [isLoadingReport, setIsLoadingReport] = useState(false);
  const [reportError, setReportError] = useState('');
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [memberExport, setMemberExport] = useState(null);
  const [exportError, setExportError] = useState('');
  const exportPollRef = useRef(null);

  const canEditPlans = !!(user && user.role === ROLES.BRANCH_MANAGER);

  const stopExportPolling = () => {
    if (exportPollRef.current) {
      clearTimeout(exportPollRef.current);
      exportPollRef.current = null;
    }
  };

  // Clear any in-flight poll timer on unmount.
  useEffect(() => stopExportPolling, []);

  const downloadExport = (exportRecord) => {
    api.downloadBranchMembersExport(exportRecord.id, exportRecord.filename)
      .catch(() => setExportError('The export file could not be downloaded. Please export again.'));
  };

  // Poll the background job every few seconds; on completion notify and start the
  // download automatically. Generating the file is the slow part (it can cover millions
  // of members), so all the frontend ever does is these cheap status reads.
  const pollExport = (exportId) => {
    exportPollRef.current = setTimeout(() => {
      api.fetchBranchMembersExport(exportId)
        .then((exported) => {
          setMemberExport(exported);
          if (exported.status === 'completed') {
            downloadExport(exported);
          } else if (exported.status === 'failed') {
            setExportError(exported.errorMessage || 'The export failed. Please try again.');
            setMemberExport(null);
          } else {
            pollExport(exportId);
          }
        })
        .catch(() => {
          setExportError('Lost track of the export. Please try again.');
          setMemberExport(null);
        });
    }, 3000);
  };

  const handleExportMembers = () => {
    if (!selection) return;
    stopExportPolling();
    setExportError('');
    setMemberExport({ status: 'queued' });

    api.createBranchMembersExport(selection)
      .then((exported) => {
        setMemberExport(exported);
        if (exported.status === 'completed') {
          downloadExport(exported);
        } else {
          pollExport(exported.id);
        }
      })
      .catch((err) => {
        const message = err.response
          && err.response.data
          && err.response.data.errors
          && err.response.data.errors.message;
        setExportError(message || 'Could not start the export. Please try again.');
        setMemberExport(null);
      });
  };

  // Re-usable loader: used both for the initial picker "Load" and to refresh after a plan
  // edit (clearReport=false keeps the current report visible while it reloads).
  const loadReport = (nextSelection, { clearReport = true } = {}) => {
    setIsLoadingReport(true);
    setReportError('');
    if (clearReport) setReport(null);
    setHasLoaded(true);

    return api.fetchBranchDashboardReport({
      enrollmentYearId: nextSelection.enrollmentYearId,
      branchId: nextSelection.branchId,
      administrativeDivisionId: nextSelection.administrativeDivisionId,
    })
      .then((data) => {
        setReport(data);
      })
      .catch((err) => {
        const message = err.response
          && err.response.data
          && err.response.data.errors
          && err.response.data.errors.message;
        setReportError(message || err.message || 'Failed to load dashboard');
      })
      .finally(() => {
        setIsLoadingReport(false);
      });
  };

  const handleLoad = (nextSelection) => {
    setSelection(nextSelection);
    // An export belongs to one scope; drop it (and its poller) when the scope changes.
    stopExportPolling();
    setMemberExport(null);
    setExportError('');
    loadReport(nextSelection);
  };

  // After saving targets, pull a fresh report so plan-vs-actual reflects the new numbers.
  const handlePlanSaved = () => {
    if (selection) loadReport(selection, { clearReport: false });
  };

  // The plan editor works against the scope the report was loaded for; build it from the
  // report payload (it carries the resolved names) falling back to the raw selection.
  const editorScope = report
    ? {
      enrollmentYearId: report.enrollmentYearId,
      enrollmentYear: report.enrollmentYear,
      branchId: report.branchId,
      branchName: report.branchName,
      administrativeDivisionId: report.administrativeDivisionId,
      administrativeDivisionName: report.administrativeDivisionName,
    }
    : null;

  return (
    <>
      <Head title="Branch Dashboard" />
      <Menu supportsMobile />
      <Page>
        <BranchAdministrativeDivisionPicker
          initialBranchId={user && user.branchId}
          autoLoad={canEditPlans}
          autoEnrollmentYearId={canEditPlans ? 6 : undefined}
          onLoad={handleLoad}
          isLoadingLoad={isLoadingReport}
        />

        <Content>
          {isLoadingReport && (
            <StateCard>
              <LoadingIndicator noun="dashboard" />
            </StateCard>
          )}

          {!isLoadingReport && reportError && (
            <Box marginBottom={4}>
              <Alert>{reportError}</Alert>
            </Box>
          )}

          {!isLoadingReport && exportError && (
            <Box marginBottom={4}>
              <Alert>{exportError}</Alert>
            </Box>
          )}

          {!isLoadingReport && !reportError && report && (
            <BranchDashboardReport
              report={report}
              canEdit={canEditPlans}
              onEditPlan={() => setIsEditorOpen(true)}
              memberExport={memberExport}
              onExportMembers={handleExportMembers}
              onDownloadExport={() => memberExport && downloadExport(memberExport)}
            />
          )}

          {!isLoadingReport && !reportError && !report && (
            <HintCard>
              {hasLoaded
                ? 'No data available for this selection.'
                : 'Choose an enrollment year, branch, and administrative division above, then select “Load” to view the report.'}
            </HintCard>
          )}
        </Content>
      </Page>

      {isEditorOpen && editorScope && (
        <BranchPlanEditor
          scope={editorScope}
          onClose={() => setIsEditorOpen(false)}
          onSaved={handlePlanSaved}
        />
      )}
    </>
  );
}

BranchDashboardView.propTypes = {
  user: PropTypes.shape({
    branchId: PropTypes.number,
    role: PropTypes.string,
  }),
};

BranchDashboardView.defaultProps = {
  user: null,
};

export default connect(state => ({
  user: userSelector(state),
}))(BranchDashboardView);
